import { Injectable, NgZone } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import {
  catchError,
  EMPTY,
  finalize,
  firstValueFrom,
  mergeMap,
  Observable,
  of,
  throwError,
  withLatestFrom,
} from 'rxjs';
import {
  ButtonTitle,
  Schedule,
  ScheduleTitle,
  Schedule_Paging,
} from '@app/models/schedule.model';
import { Store } from '@ngxs/store';
import { DestroySpinner, HideSpinner, ShowSpinner } from '@app/store/spinner/spinner.classes';
import { environment } from 'src/environments/environment';
import { DateService } from './date.service';
import { AuthSelectors } from '@app/store/auth/auth.selectors';
import { RefreshToken } from '@app/store/auth/auth.classes';
import { DialogService } from './dialog.service';
import {
  MenuParam,
  RecordListResponse,
  RecordTitleSet,
  DisplaySetting,
  BaseTimeRecordListResponse,
  RecordListConfigPayload,
  RecordTagSet,
} from '@app/models/record.model';
import { CareConneCondition, Note, NoteUpdate, NoteUpdateResponse, NoteUpdDatePayload } from '@app/models/note.model';
import { AuthService } from './auth.service';
import { Menu } from '@app/models/menu.model';
import { MessageService } from './message.service';
import { UserBasicInfo } from '@app/models/basicInfo.model';
import { CompFuncRoleCondition } from '@app/models/bd-role.model';
import { AddTitle } from '@app/store/title/title.classes';
import { TitleModel } from '@app/store/title/title.state';
import { TitleSelectors } from '@app/store/title/title.selectors';
import * as moment from 'moment';
import { WebviewService } from './webview.service';
import { SettingSelectors } from '@app/store/setting/setting.selectors';
import { NoteBlobReq, S3NoteBlobsByField, S3NoteBlobsData } from '@app/models/noteblob.model';
import { requestToFacilityuserList, requestToNoteUpdate, requestToScheduleResult } from '@app/shared/common/utils';
import { OfficeService } from './office.service';
import { DialogData } from '@app/shared/components/confirm-dialog/confirm-dialog.component';

interface ScheduleResult {
  objkbn: string;
  objcd: string;
  noteid: number;
  resultatr: number;
  upddate: Date;
  displayName: string;
}

export interface CareConneRelateRes { status: boolean, message: string, statusCode: number }

interface NoteGpsUpdate {
  objkbn: string;
  objcd: string;
  odrstfcd?: string;
  odrstfnm?: string;
  odrunitcd: string;
  odrseccd: string;
  odrteamcd: string;
  odrjobcd: string;
  time: string;
  latitude: string;
  longitude: string;
  label: string;
}
/**
 * using for FE error that can not be seen in api (not backend logic)
 */
interface FrontEndWatchLog {
  location: string;
  contents: string;
}

interface NoteListDefault {
  sdate: Date;
  objcd: string;
  titleSetKeys: {
    obj: string;
    kbn: string;
    cd: string;
  }[];
}

interface Accesslog {
  accstyp: number;
  content?: string;
  displayName?: string;
}

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
};

interface ScheduleConditionFilter {
  stfcd: string,
  startDate: Date,
  endDate: Date,
  skip?: number,
  spinner?: boolean,
  roleKey?: string,
  roleContents?: string[],
  daicd?: string,
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(
    private http: HttpClient,
    private store: Store,
    private dateService: DateService,
    private message: MessageService,
    private dialog: DialogService,
    private ngZone: NgZone,
    private authService: AuthService,
    private webviewService: WebviewService,
    private officeService: OfficeService
  ) { }
  monoDialog: boolean = false;
  getTeamStaffList() {
    const url = `${environment.KIDS_CLIENT_API}/team/staff/list`;
    return this.get<any>(url);
  }

  getUserList() {
    const url = `${environment.KIDS_CLIENT_API}/facilityuser/list`;
    return this.get<any>(url);
  }

  getUserInGroupList(payload: any) {
    const url = `${environment.KIDS_CLIENT_API}/team/useringroup`;
    return this.post<any>(url, payload);
  }

  getStaffInTeamList(payload: any) {
    const url = `${environment.KIDS_CLIENT_API}/team/staffinteam`;
    return this.post<any>(url, payload);
  }

  getUserImage(daicd: string, upddate?: string) {
    const url = `${environment.KIDS_CLIENT_API}/facilityuser/blob/${daicd}`;
    return this.post<any>(url, { upddate }).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) return of({});
        return EMPTY;
      })
    );
  }

  async fetchTitleData(condition: any, checkExist: boolean = true) {
    if (!condition) return;
    let { officecd, corpcd, subcd, blockcd, unitcd, ttlkbn, ttlcd, sdate } = condition;
    if (checkExist) {
      const list: TitleModel[] = this.store.selectSnapshot(TitleSelectors.titleList);
      const findTitle = list.find(x => x.ttlkbn === ttlkbn && x.ttlcd === ttlcd);
      if (findTitle) { return findTitle }
    }
    let param = {
      officecd, corpcd, subcd, blockcd, unitcd, ttlkbn, ttlcd,
      sdate: moment(sdate).startOf('day').format('YYYY-MM-DDTHH:mm:ss')
    }
    let title = await this.getTitle(param);
    if (title) {
      this.store.dispatch(new AddTitle(title));
      return title;
    }
  }

  async getTitle(params: any) {
    const corpPrefix = localStorage.getItem('corpPrefix');
    const url = `${environment.KIDS_CLIENT_API}/titles/getone`;
    const titleParam = { ...params, corpPrefix };
    const title = await this.post<any>(url, titleParam).toPromise();
    return title

  }

  getUserBasic(daicd: string) {
    const url = `${environment.KIDS_CLIENT_API}/facilityuser/basic/${daicd}`;
    return this.get<UserBasicInfo>(url);
  }

  getAliasList() {
    const url = `${environment.KIDS_CLIENT_API}/setting/display-alias/list`;
    return this.get<any>(url);
  }

  getButtonTitleList(_corpPrefix: string = "") {
    const corpPrefix = localStorage.getItem('corpPrefix') || _corpPrefix;
    const url = `${environment.KIDS_CLIENT_API}/setting/buttonttl/list`;
    return this.post<ButtonTitle[]>(url, { corpPrefix });
  }

  getScheduleTitleList() {
    const url = `${environment.KIDS_CLIENT_API}/schedule/title/list`;
    return this.get<ScheduleTitle[]>(url);
  }

  getDailyScheduleList(date: Date, shouldThrow: boolean = true): Observable<Schedule[]> {
    const d = this.dateService.format(date, 'yyyyMMdd');
    const url = `${environment.KIDS_CLIENT_API}/schedule/daily/${d}`;
    return this.get<Schedule[]>(url, shouldThrow);
  }

  getScheduleList(
    // stfcd: string,
    // startDate: Date,
    // endDate: Date,
    // skip?: number,
    // spinner?: boolean,
    // roleKey?: string
    scheduleFilter: ScheduleConditionFilter
  ): Observable<Schedule_Paging> {
    const url = `${environment.KIDS_CLIENT_API}/schedule/list`;
    const { stfcd, skip, roleKey, spinner, daicd, startDate, endDate, roleContents } = scheduleFilter;
    const sdate = this.dateService.formatISO8601(startDate);
    const edate = this.dateService.formatISO8601(endDate);
    return this.post<Schedule_Paging>(url, { stfcd, sdate, edate, skip, roleKey, daicd, roleContents }, spinner);
  }

  setScheduleResult({
    objkbn,
    objcd,
    noteid,
    resultatr,
    upddate,
    displayName,
  }: ScheduleResult): Observable<Note> {
    const url = `${environment.KIDS_CLIENT_API}/schedule/result`;
    const payload = { objkbn, objcd, noteid, resultatr, upddate, displayName };
    const shouldThrow = true;
    return this.post<Note>(url, payload, shouldThrow).pipe(
      mergeMap((note) => {
        if (note.resultatr === resultatr) {
          return of(note);
        }
        this.ngZone.run(() =>
          this.dialog.message('予定を保存できませんでした')
        );
        return throwError(() => { });
      })
    );
  }

  getScheduleDetail(payload: {
    objkbn: string;
    objcd: string;
    noteid: number;
  }): Observable<Note[]> {
    const url = `${environment.KIDS_CLIENT_API}/schedule/detail`;
    return this.post<Note[]>(url, payload);
  }

  getNoteList(
    payload: { objkbn: string; objcd: string; noteid: number }[]
  ): Observable<Note[]> {
    const url = `${environment.KIDS_CLIENT_API}/note/list/keys`;
    return this.post<Note[]>(url, payload);
  }

  getFaceSheet(
    payload: { objkbn: string; objcd: string; noteid: number | undefined, IsDecideEndDate?: boolean }[]
  ): Observable<Note[]> {
    const url = `${environment.KIDS_CLIENT_API}/note/title-report`;
    return this.post<Note[]>(url, payload);
  }

  notesNullCheck(notes: { v2contents?: string; contents?: string; ttlkbn?: string; ttlcd?: string }[], location: string) {
    // check case noteDatas is empty, null or contain null data
    if (notes?.some((x) => { return (!x.v2contents && !x.contents) })) {
      // create location
      let notesfind = notes.filter((x) => { return (!x.v2contents && !x.contents) });
      let contents = notesfind.map((x) => { return `nulled when create ${x.ttlkbn}-${x.ttlcd}` }).join(' - ');
      this.postErrorLog({ location, contents }).subscribe(
        (res) => {
          if (res) {
            console.log('UNEXPECTED LOGGED')
          }
        }
      );
    }
    //
  }

  setNoteList(payload: NoteUpdate[]): Observable<NoteUpdateResponse[]> {
    this.notesNullCheck(payload.map((x) => {
      const { v2contents, contents, ttlkbn, ttlcd } = x.note;
      return { v2contents, contents, ttlkbn, ttlcd };
    }), 'setNoteList');
    const url = `${environment.KIDS_CLIENT_API}/note/update`;
    return this.post<NoteUpdateResponse[]>(url, payload).pipe(
      mergeMap((res: NoteUpdateResponse[]) => {
        if (res) {
          return of(res);
        }
        return EMPTY;
      })
    );
  }

  setNoteGpsUpate(payload: NoteGpsUpdate): Observable<void> {
    const url = `${environment.KIDS_CLIENT_API}/note/gps/update`;
    return this.post<void>(url, payload);
  }

  getNoteListDefault(payload: NoteListDefault): Observable<Note[]> {
    const url = `${environment.KIDS_CLIENT_API}/note/list/default`;
    const sdate = this.dateService.formatISO8601(payload.sdate);
    return this.post<Note[]>(url, { ...payload, sdate });
  }

  postErrorLog(payload: FrontEndWatchLog): Observable<any> {
    const url = `${environment.KIDS_CLIENT_API}/admin/kids-fe-error`;
    return this.post<FrontEndWatchLog>(url, payload);
  }

  async getRecordList(
    param: MenuParam & DisplaySetting
  ): Promise<RecordListResponse> {
    const { sdate, edate } = param;
    const payload = {
      ...param,
      sdate: sdate ? this.dateService.formatISO8601(sdate) : null,
      edate: edate ? this.dateService.formatISO8601(edate) : null,
    };
    const url = `${environment.KIDS_CLIENT_API}/record/list-optimize`;
    let result: any = {};
    // self reload is handled in component
    result = await this.post<RecordListResponse>(url, payload).toPromise();
    return result;
  }

  getMenuList(_corpPrefix: string = ""): Observable<Menu[]> {
    const corpPrefix = localStorage.getItem('corpPrefix') || _corpPrefix;
    const url = `${environment.KIDS_CLIENT_API}/common/menu`;
    return this.post<Menu[]>(url, { corpPrefix });
  }

  getFormHistoryList(payload: {
    daicd: string;
    objcd: string | null;
    ttlkbn: string | null;
    ttlcd: string | null;
  }): Observable<Note[]> {
    const url = `${environment.KIDS_CLIENT_API}/form/history/list`;
    return this.post<Note[]>(url, payload);
  }

  setAccesslog(payload: Accesslog): Observable<void> {
    const url = `${environment.KIDS_CLIENT_API}/accesslog/update`;
    return this.post<void>(url, payload);
  }

  checkRoleByStaff(): Observable<CompFuncRoleCondition[]> {
    const url = `${environment.KIDS_CLIENT_API}/role/check-role-by-staff`;
    return this.get<CompFuncRoleCondition[]>(url);
  }

  private get<T>(url: string, shouldThrow?: boolean, shouldSpinner: boolean = true) {
    this.verifyToCustomHeaders(url);
    const req = () => this.http.get<T>(url, httpOptions);
    return this.handleReq(req, shouldThrow, shouldSpinner);
  }

  private put<T>(url: string, body: any, shouldThrow?: boolean, shouldSpinner: boolean = true) {
    const req = () => this.http.put<T>(url, body, { headers: { 'Content-Type': body?.type || 'image/jpeg' } });
    return this.handleReq(req, shouldThrow, shouldSpinner);
  }

  private post<T>(url: string, body: any, shouldThrow?: boolean, shouldSpinner: boolean = true) {
    this.verifyToCustomHeaders(url);
    const req = () => this.http.post<T>(url, body, httpOptions);
    return this.handleReq(req, shouldThrow, shouldSpinner);
  }

  private handleReq<T>(req: () => Observable<T>, shouldThrow: boolean = false, shouldSpinner: boolean = true) {
    const token = this.authService.getAccessToken();
    const objectcd = this.store.selectSnapshot(AuthSelectors.objectcd);
    httpOptions.headers = httpOptions.headers
      .set('Authorization', token ?? '')
      .set('objectcd', objectcd);
    if (shouldSpinner) {
      this.store.dispatch(new ShowSpinner());
    }

    if (this.authService.isAuthenticated()) {
      return req().pipe(
        catchError(this.handleError<T>(shouldThrow, { req })),
        finalize(() => {
          if (shouldSpinner) {
            this.store.dispatch(new HideSpinner())
          }
        })
      );
    }
    // const isWebView = this.store.selectSnapshot(SettingSelectors.isWebView);

    // if (isWebView) {
    //   this.store.dispatch(new HideSpinner());
    //   this.webviewService.kidsExpireToken();
    //   return EMPTY;
    // }
    const accessToken$ = this.store.select(AuthSelectors.accessToken);
    return this.store.dispatch(new RefreshToken()).pipe(
      withLatestFrom(accessToken$),
      mergeMap(([_, accessToken]) => {
        if (accessToken) {
          httpOptions.headers = httpOptions.headers.set(
            'Authorization',
            accessToken
          );
          return req().pipe(
            catchError(this.handleError<T>(shouldThrow)),
            finalize(() => {
              if (shouldSpinner) {
                this.store.dispatch(new DestroySpinner())
              }
            })
          );
        }
        return EMPTY;
      })
    );
  }

  private handleError<T>(
    shouldThrow: boolean,
    param?: { req: () => Observable<T> }
  ) {
    return (error: HttpErrorResponse): Observable<T> => {
      console.error(error);

      if (param && error.status === 401) {
        let webview = this.store.selectSnapshot(SettingSelectors.isWebView);
        if (webview) {
          this.webviewService.kidsExpireToken();
          return EMPTY;
        }
        const accessToken$ = this.store.select(AuthSelectors.accessToken);
        return this.store.dispatch(new RefreshToken()).pipe(
          withLatestFrom(accessToken$),
          mergeMap(([_, accessToken]) => {
            if (accessToken) {
              httpOptions.headers = httpOptions.headers.set(
                'Authorization',
                accessToken
              );
              return param
                .req()
                .pipe(catchError(this.handleError<T>(shouldThrow)));
            }
            return EMPTY;
          })
        );
      }
      // webview error handler
      this.webviewService.kidsError(JSON.stringify(error));
      const text = this.message.getHttpErrorMessage(error);
      if (!this.monoDialog) {
        this.monoDialog = true;
        this.ngZone.run(() => {
          if (typeof text === 'object') {
            this.displayConfirmDialog(text.message);
            return;
          }
          this.dialog.message(text).afterClosed().subscribe(() => this.monoDialog = false);
        });
      }
      if (shouldThrow) return throwError(() => error);
      return EMPTY;
    };
  }

  checkSyncS3() {
    const corpPrefix = localStorage.getItem('corpPrefix');
    const url = `${environment.KIDS_CLIENT_API}/admin/check-async-s3`;
    return this.post<any>(url, { corpPrefix });
  }

  getStfBDSetting(request: any) {
    request.corpPrefix = localStorage.getItem('corpPrefix');
    const url = `${environment.KIDS_CLIENT_API}/setting/dynamodb/get-stf-bd-setting`;
    return this.post<any>(url, request);
  }
  setStfBDSetting(request: any) {
    if (!request) return of(null);
    request.corpPrefix = localStorage.getItem('corpPrefix');
    const url = `${environment.KIDS_CLIENT_API}/setting/dynamodb/update-stf-bd-setting`;
    return this.post<any>(url, request);
  }

  initStfBDSetting(request: any) {
    if (!request) return of(null);
    request.corpPrefix = localStorage.getItem('corpPrefix');
    const url = `${environment.KIDS_CLIENT_API}/setting/dynamodb/insert-stf-bd-setting`;
    return this.post<any>(url, request);
  }


  getOfficeSetting() {
    const url = `${environment.KIDS_CLIENT_API}/setting/getbdsysenv-officest`;
    return this.get<any>(url);
  }

  fetchBaseTimeRecordList(param?: MenuParam): Observable<BaseTimeRecordListResponse> {
    const payload = {
      ...param,
    };
    const url = `${environment.KIDS_CLIENT_API}/record/get-base-time-record-list`;
    return this.post<BaseTimeRecordListResponse>(url, payload);
  }

  async fetchOfficeSetting() {
    const url = `${environment.KIDS_CLIENT_API}/setting/getbdsysenv-officest`;
    return await this.get<any>(url).toPromise();
  }

  verifyToCustomHeaders(url: string) {
    const isRequestToNoteUpdate = requestToNoteUpdate(url);
    const isRequestToScheduleResult = requestToScheduleResult(url);
    const isRequestToFacilityuserList = requestToFacilityuserList(url);
    if (
      isRequestToNoteUpdate || 
      isRequestToScheduleResult || 
      isRequestToFacilityuserList
    ) {
      const companyId = this.store.selectSnapshot(AuthSelectors.selectedUnit)?.companyId;
      const corpPrefix = localStorage.getItem('corpPrefix');
      const deviceId = localStorage.getItem('deviceId');
      httpOptions.headers = httpOptions.headers.set('companyId', companyId?.toString() ?? '');
      httpOptions.headers = httpOptions.headers.set('corpPrefix', corpPrefix?.toString() ?? '');
      httpOptions.headers = httpOptions.headers.set('deviceId', deviceId?.toString() ?? '');
    }
    else {
      httpOptions.headers = new HttpHeaders({
        'Content-Type': 'application/json',
      });
    }
  }
  
  async fetchUpdDateByNotes(payload: NoteUpdDatePayload[]) {
    const url = `${environment.KIDS_CLIENT_API}/note/get-upddate-by-notes`;
    return await this.post<NoteUpdDatePayload[]>(url, payload).toPromise();
  }
  async createPresignUrl(payload: NoteBlobReq): Promise<S3NoteBlobsData | null> {
    const url = `${environment.KIDS_CLIENT_API}/noteblob/presign-url`;
    return await this.promiseHandler<S3NoteBlobsData>(this.post<S3NoteBlobsData>(url, payload))
  }

  async createPresignUrls(payload: NoteBlobReq): Promise<S3NoteBlobsData | null> {
    const url = `${environment.KIDS_CLIENT_API}/noteblob/presign-urls`;
    return await this.promiseHandler<S3NoteBlobsData>(this.post<S3NoteBlobsData>(url, payload))
  }

  async putByPresignUrl(presignUrl: string, payload: any) {
    return await this.promiseHandler<S3NoteBlobsData>(this.put<S3NoteBlobsData>(presignUrl, payload))
  }

  async setNoteBlobs(payload: NoteBlobReq): Promise<S3NoteBlobsByField[]> {
    const url = `${environment.KIDS_CLIENT_API}/note/blobv2/list`;
    return await this.promiseHandler<S3NoteBlobsByField[]>(this.post<S3NoteBlobsByField[]>(url, payload)) || [];
  }

  async fetchRecordList(payload: RecordListConfigPayload) {
    const url = `${environment.KIDS_CLIENT_API}/record/kids/list`;
    return await this.post<any>(url, payload).toPromise();
  }

  getMstype(): Observable<string[]> {
    const url = `${environment.KIDS_CLIENT_API}/setting/mstype`;
    return this.get<string[]>(url);
  }

  getRecordTitleSetList(id: string) {
    const url = `${environment.KIDS_CLIENT_API}/title-alias/list`;
    return this.get<RecordTitleSet[]>(url);
  }

  getRecordTagList(id: string) {
    const url = `${environment.KIDS_CLIENT_API}/record/title-tag/list`;
    return this.get<RecordTagSet[]>(url);
  }

  getCareConneRoomList(pass: number, daicd?: string) {
    let endPoint = `${pass}${daicd ? `?daicd=${daicd}` : ''}`;
    const url = `${environment.KIDS_CLIENT_API}/staff/careconne-room/${endPoint}`;
    return this.get<any>(url);
  }

  getCareConnePass() {
    const url = `${environment.KIDS_CLIENT_API}/staff/careconne-pass`;
    return this.get<any>(url);
  }

  isCareConneUnit() {
    const url = `${environment.KIDS_CLIENT_API}/unit/iscareconne`;
    return this.get<any>(url);
  }

  async recordSendToCareConne(payload: CareConneCondition) {
    const url = `${environment.KIDS_CLIENT_API}/kids/record-send`;
    return await this.promiseHandler<any>(
      this.post<any>(url, payload).pipe(
        catchError((err) => {
          console.log('error get recordSendToCareConne');
          throw (err)
        }))
    );
  }

  async sendToS3CareConne(payload: FormData) {
    try {
      const url = `${environment.KIDS_CLIENT_API}/kids/forward-s3`;
      return await this.promiseHandler<any>(this.customCareConne<any>(url, payload).pipe(
        catchError((err) => {
          console.log('error get sendToS3CareConne');
          throw (err)
        }))
      )
    } catch (error) {
      throw (error);
    }
  }

  async sendToRoomCareConne(payload: FormData): Promise<{ status: boolean, message: string }> {
    try {
      const url = `${environment.KIDS_CLIENT_API}/kids/forward-to-room`;
      return await this.promiseHandler<any>(this.customCareConne<any>(url, payload).pipe(
        catchError(() => {
          throw ('error get sendToRoomCareConne');
        })));
    } catch (error) {
      throw (error);
    }
  }
  async sendMsgToRoomCareConne(payload: FormData): Promise<CareConneRelateRes> {
    try {
      const url = `${environment.KIDS_CLIENT_API}/kids/forward-msg`;
      return await this.promiseHandler<any>(this.customCareConne<any>(url, payload))
    } catch (error) {
      throw (error);
    }
  }

  private customCareConne<T>(url: string, body: any) {
    const token = this.store.selectSnapshot(AuthSelectors.accessToken);
    const pass = this.store.selectSnapshot(SettingSelectors.CareConnePass);
    const stf = this.store.selectSnapshot(AuthSelectors.profile);
    const objectcd = this.officeService.officecd;
    let headers = new HttpHeaders({
      'Authorization': token ?? '',
      'senderId': pass.toString(),
      'objectcd': objectcd ?? '',
      'stfcd': stf?.staffCode ?? ''
    });
    const throwData = (err: any) => {
      throw (err);
    }
    const req = () => this.http.post<T>(url, body, { headers }).pipe(catchError((err) => throwData(err)));
    return this.handleReq(req, true, true).pipe(catchError((error: any) => {
      // case exceed limit size of file
      let errRes: CareConneRelateRes = {
        status: false,
        message: error.message,
        statusCode: 413
      }
      throw (errRes);

    }));
  }

  async promiseHandler<T>(observableProcessing: Observable<any>): Promise<T | null> {
    return new Promise<T | null>((res, _) => {
      try {
        observableProcessing.subscribe((data: T) => {
          res(data);
        })
      } catch (error) {
        res(null);
      }
    })
  }

  displayConfirmDialog(msg: string) {
    const data: DialogData = {
      cancelText: '',
      excuteText: '閉じる',
      content: `<p style="white-space: break-spaces;'font-size': '12px'">${msg || 'ケアコネ転送に失敗しました。'}</p>`,
      style: {
        'text-align': 'center',
      }
    };
    return this.dialog.confirm(data).afterClosed().subscribe(() => this.monoDialog = false);
  }

  async getDataByKey(key: string) {
    const url = `${environment.KIDS_CLIENT_API}/media/get-data/${key}`;
    return await firstValueFrom(this.http.get<any>(url));
  }
}

